import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DesktopConfig, MfaPcConfig } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { audio } from "../../lib/audio";
import type { MfitDone, MfitGuideFn } from "../mfit/shared";
import { setDeskBuzz, vibrate } from "./sfx";
import "./mfa.css";

/**
 * Alex's office PC in "Approve or Deny".
 *  signin   desktop + task -> Browser -> intranet -> Finance Portal ->
 *           Microsoft "Approve sign-in request" with the number.
 *  portal   the Finance Portal after Alex's own sign-in.
 *  sheet    intranet documents -> the Q3 spreadsheet -> fill in a cell;
 *           then the phone buzzes on the desk.
 *  upload   calendar reminder -> Upload report -> step-up number.
 *  uploaded the upload has gone through.
 */
type Stage =
  | "desktop"
  | "intranet"
  | "msnumber"
  | "portal"
  | "docs"
  | "sheet"
  | "typing"
  | "buzz"
  | "upload"
  | "stepup"
  | "info";

const DESK_APPS = [
  { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  { id: "outlook", label: "Outlook", icon: "outlook", opens: "outlook" },
  { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
  { id: "files", label: "File Explorer", icon: "files", opens: "files" },
];

export function MfaPc({ config, onDone, onGuideChange }: { config: MfaPcConfig; onDone: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  const start: Stage =
    config.mode === "signin" || config.mode === "sheet"
      ? "desktop"
      : config.mode === "upload"
      ? "upload"
      : "info";
  const [stage, setStage] = useState<Stage>(start);
  const [typed, setTyped] = useState("");
  const [toast, setToast] = useState(config.mode === "signin" && !!config.task);
  const [calendar, setCalendar] = useState(false);
  const doneRef = useRef(false);
  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };

  const guideKey = stage === "upload" && !calendar && config.calendar ? "upload_wait" : stage;
  useEffect(() => {
    onGuideChange?.(config.guide?.[guideKey] ?? null);
  }, [guideKey, config.guide, onGuideChange]);
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);

  /** A page or tile opens inside the browser. */
  const go = (next: Stage) => {
    audio.ui("pageOpen");
    setStage(next);
  };

  useEffect(() => {
    if (toast) audio.ui("reminder");
    if (config.mode !== "upload" || !config.calendar) return;
    const t = window.setTimeout(() => {
      setCalendar(true);
      audio.ui("reminder");
    }, 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filling in the spreadsheet, then the phone starts buzzing on the desk.
  useEffect(() => {
    if (stage !== "typing" || !config.sheet) return;
    const v = config.sheet.fillValue;
    const timers: number[] = [];
    for (let i = 1; i <= v.length; i++) {
      timers.push(
        window.setTimeout(() => {
          setTyped(v.slice(0, i));
          if (i % 2 === 1) audio.typing(1);
        }, 160 * i),
      );
    }
    timers.push(window.setTimeout(() => setStage("buzz"), 160 * v.length + 1600));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [stage, config.sheet]);
  useEffect(() => {
    if (stage !== "buzz") return;
    audio.ui("alert");
    vibrate();
    setDeskBuzz(true);
    const t = window.setInterval(() => vibrate(0.35, 2), 3200);
    return () => {
      window.clearInterval(t);
    };
  }, [stage]);
  // the sign-in pages: the phone gets the request a moment after the number appears
  useEffect(() => {
    if (stage !== "msnumber" && stage !== "stepup") return;
    const t = window.setTimeout(() => {
      audio.ui("alert");
      vibrate();
      setDeskBuzz(true);
    }, 1400);
    return () => window.clearTimeout(t);
  }, [stage]);

  const browserOpen = stage !== "desktop";
  const deskConfig: DesktopConfig = { clock: config.clock, date: config.date, icons: DESK_APPS, taskbar: DESK_APPS };
  const portalHost = config.portal.url;
  const url =
    stage === "intranet" || stage === "docs"
      ? config.sheet?.intranetUrl ?? "intranet.northbay.internal"
      : stage === "sheet" || stage === "typing" || stage === "buzz"
      ? config.sheet?.fileUrl ?? "docs.google.com"
      : stage === "msnumber" || stage === "stepup"
      ? "login.microsoftonline.com"
      : portalHost;
  const title =
    stage === "sheet" || stage === "typing" || stage === "buzz"
      ? `${config.sheet?.fileName ?? "Sheet"} — Google Sheets`
      : stage === "msnumber" || stage === "stepup"
      ? "Sign in to your account"
      : stage === "intranet" || stage === "docs"
      ? "NorthBay Intranet"
      : config.portal.name;

  const clickHere = (
    <em className="mfx-clickhere" aria-hidden="true">
      <i>←</i> Click here
    </em>
  );

  const phoneButton = (
    <button type="button" className="mfit-btn mfit-btn--primary cf-pulse-target mfx-phonebtn" onClick={() => finish({ key: "phone" })}>
      {config.phoneLabel ?? "Pick up your phone"}
    </button>
  );

  let page: ReactNode = null;
  if (stage === "intranet") {
    page = (
      <div className="mfit-intra">
        <header className="mfit-intra-h">
          <b>NorthBay Intranet</b>
          <nav>
            <span>News</span>
            <span>People</span>
            <span>IT help</span>
            <span>Documents</span>
          </nav>
        </header>
        <div className="mfit-intra-body">
          <h2>Good morning, Alex</h2>
          <div className="mfx-tiles">
            {config.mode === "signin" ? (
              <button type="button" className="mfx-tile cf-pulse-target" onClick={() => go("msnumber")}>
                <span className="mfx-tile-ico is-portal" aria-hidden="true" />
                <b>
                  {config.portal.name}
                  {clickHere}
                </b>
                <small>Reports, budgets and approvals</small>
              </button>
            ) : (
              <div className="mfx-tile is-off">
                <span className="mfx-tile-ico is-portal" aria-hidden="true" />
                <b>{config.portal.name}</b>
                <small>Signed in</small>
              </div>
            )}
            <button
              type="button"
              className={`mfx-tile${config.mode === "sheet" ? " cf-pulse-target" : " is-off"}`}
              onClick={() => config.mode === "sheet" && go("docs")}
              disabled={config.mode !== "sheet"}
            >
              <span className="mfx-tile-ico is-docs" aria-hidden="true" />
              <b>
                Documents
                {config.mode === "sheet" && clickHere}
              </b>
              <small>Shared team files</small>
            </button>
            <div className="mfx-tile is-off">
              <span className="mfx-tile-ico is-help" aria-hidden="true" />
              <b>IT help</b>
              <small>+41 44 500 22 00</small>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (stage === "docs" && config.sheet) {
    page = (
      <div className="mfit-intra">
        <header className="mfit-intra-h">
          <b>Documents · Finance</b>
        </header>
        <div className="mfit-intra-body">
          <ul className="mfx-files">
            <li>
              <button type="button" className="mfx-file cf-pulse-target" onClick={() => go("sheet")}>
                <span className="mfx-file-ico is-sheet" aria-hidden="true" />
                <b>
                  {config.sheet.fileName}
                  {clickHere}
                </b>
                <small>Google Sheets · edited yesterday by Priya Shah</small>
              </button>
            </li>
            <li>
              <span className="mfx-file is-off">
                <span className="mfx-file-ico is-doc" aria-hidden="true" />
                <b>Q3 commentary draft</b>
                <small>Google Docs · edited 3 days ago</small>
              </span>
            </li>
            <li>
              <span className="mfx-file is-off">
                <span className="mfx-file-ico is-sheet" aria-hidden="true" />
                <b>Budget 2027 planning</b>
                <small>Google Sheets · edited last week</small>
              </span>
            </li>
          </ul>
        </div>
      </div>
    );
  } else if ((stage === "sheet" || stage === "typing" || stage === "buzz") && config.sheet) {
    const sh = config.sheet;
    page = (
      <div className="mfx-sheet">
        <div className="mfx-sheet-bar">
          <span className="mfx-file-ico is-sheet" aria-hidden="true" />
          <b>{sh.fileName}</b>
          <span className="mfx-sheet-menu">File Edit View Insert Format Data Tools</span>
        </div>
        <div className="mfx-sheet-fx">
          <span>fx</span>
          {stage === "sheet" ? "" : typed}
        </div>
        <div className="mfx-grid" style={{ gridTemplateColumns: `2.4em repeat(${sh.columns.length}, 1fr)` }}>
          <span className="mfx-gh" />
          {sh.columns.map((c, i) => (
            <span key={c} className="mfx-gh">
              {String.fromCharCode(65 + i)}
            </span>
          ))}
          <span className="mfx-gh">1</span>
          {sh.columns.map((c) => (
            <span key={c} className="mfx-gc is-head">
              {c}
            </span>
          ))}
          {sh.rows.map((r, ri) => (
            <RowCells key={ri} n={ri + 2} cells={r} fill={ri === sh.fillRow ? sh.fillCol : -1} value={stage === "sheet" ? "" : typed} />
          ))}
        </div>
        {stage === "sheet" && (
          <div className="mfx-sheet-task">
            <span>The Q3 revenue cell is still empty.</span>
            <button type="button" className="mfit-btn mfit-btn--primary cf-pulse-target" onClick={() => {
                audio.ui("pageOpen");
                setStage("typing");
              }}>
              {sh.fillLabel}
            </button>
          </div>
        )}
        {stage === "buzz" && (
          <div className="mfx-sheet-task is-buzz">
            <span>Your phone is vibrating on the desk. You did not start a sign-in.</span>
            {phoneButton}
          </div>
        )}
      </div>
    );
  } else if (stage === "msnumber" || stage === "stepup") {
    page = (
      <div className="mfit-mspage">
        <div className="mfit-mscard mfx-mscard">
          <div className="mfit-ms-brand">
            <span className="mfit-ms" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </span>
            Microsoft
          </div>
          <div className="mfit-ms-acctline">alex.morgan@northbayfinancial.com</div>
          <h3>{stage === "stepup" ? "Verify your identity" : "Approve sign in request"}</h3>
          <p className="mfit-ms-sub">
            {stage === "stepup"
              ? `Uploading a confidential report needs another check. Open your Authenticator app, and enter the number shown to continue.`
              : "Open your Authenticator app, and enter the number shown to sign in."}
          </p>
          <div className="mfx-msnum">{config.number}</div>
          <p className="mfit-ms-sub is-small">No numbers in your app? Make sure to upgrade to the latest version.</p>
          <span className="mfit-ms-other">I can't use my Microsoft Authenticator app right now</span>
          {phoneButton}
        </div>
      </div>
    );
  } else if (stage === "upload" || stage === "info" || stage === "portal") {
    const uploaded = config.mode === "uploaded";
    page = (
      <div className="mfx-portal">
        <aside className="mfx-portal-nav">
          <b>{config.portal.name}</b>
          <span className="is-on">Reports</span>
          <span>Budgets</span>
          <span>Approvals</span>
          <span>Forecasts</span>
        </aside>
        <section className="mfx-portal-main">
          <div className="mfx-portal-k">Reports · Q3 {new Date().getFullYear()}</div>
          <div className="mfx-portal-t">Quarterly financial report</div>
          <div className="mfx-portal-row">
            <span>Q3_Financial_Report.xlsx</span>
            <span className={`mfx-chip${uploaded ? " is-ok" : ""}`}>{uploaded ? "Uploaded · shared with Management" : "Draft · due 10:00"}</span>
          </div>
          {stage === "upload" && (
            <div className="mfx-actionrow">
              <button
                type="button"
                className={`mfit-btn mfit-btn--primary${calendar ? " cf-pulse-target" : ""}`}
                onClick={() => go("stepup")}
              >
                Upload report
              </button>
              {calendar && clickHere}
            </div>
          )}
          {stage === "info" && config.info && (
            <div className="mfx-info">
              {config.info.kicker && <div className="mfx-info-k">{config.info.kicker}</div>}
              <b>{config.info.title}</b>
              {config.info.body.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <button type="button" className="mfit-btn mfit-btn--primary cf-pulse-target" onClick={() => finish({ key: "done" })}>
                {config.info.continueLabel ?? "Continue"}
              </button>
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <Desktop
      config={deskConfig}
      className="mfit-desk"
      highlightAppId={stage === "desktop" ? "browser" : undefined}
      openAppIds={browserOpen ? ["browser"] : []}
      activeAppId={browserOpen ? "browser" : null}
      onOpen={(id) => {
        if (stage === "desktop" && id === "browser") {
          setToast(false);
          setStage("intranet");
        }
      }}
    >
      {stage === "desktop" && toast && config.task && (
        <div className="mfx-task" role="status">
          <div className="mfx-task-k">{config.task.kicker}</div>
          <b>{config.task.title}</b>
          <span>{config.task.body}</span>
        </div>
      )}
      {calendar && config.calendar && (
        <div className="mfx-cal" role="status">
          <span className="mfx-cal-ico" aria-hidden="true" />
          <span>
            <b>{config.calendar.title}</b>
            <small>{config.calendar.body}</small>
          </span>
        </div>
      )}
      {browserOpen && (
        <div className="mfit-win mfit-browser">
          <div className="mfit-winbar">
            <span>{title} — Browser</span>
            <span className="mfit-winbar-ctl" aria-hidden="true">
              <i>–</i>
              <i>▢</i>
              <i>✕</i>
            </span>
          </div>
          <div className="mfit-addr">
            <span aria-hidden="true">‹ › ⟳</span>
            <span className="mfit-addr-url">
              <i aria-hidden="true">🔒</i>
              {url}
            </span>
          </div>
          <div className="mfx-page">{page}</div>
        </div>
      )}
    </Desktop>
  );
}

function RowCells({ n, cells, fill, value }: { n: number; cells: string[]; fill: number; value: string }) {
  return (
    <>
      <span className="mfx-gh">{n}</span>
      {cells.map((c, i) => (
        <span key={i} className={`mfx-gc${i === fill ? " is-fill" : ""}${i > 0 ? " is-num" : ""}`}>
          {i === fill ? value : c}
        </span>
      ))}
    </>
  );
}
